import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import searchIcon from "../media/search-icon.svg";
import sortIcon from "../media/sort-icon.svg";
import orangeSortIcon from "../media/orange-sort-icon.svg";
import deleteButtonIcon from "../media/delete-button.svg";
import trashButtonIcon from "../media/trash-button.svg";
import editButtonIcon from "../media/edit-button.svg";
import agreeButtonIcon from "../media/agree-button.svg";
import backButtonIcon from "../media/back-button.svg";
import circleIcon from "../media/circle.svg";
import { NavLink } from "react-router-dom";
import { createPortal } from "react-dom";
import { LoadingComponent } from "../App.js";
import "../styleSheet/main/components/contactTable/contactTable.scss";

const ContactTable = ({
  title,
  createButtonLabel = "Stwórz pracownika",
  createButtonLink,
  contactData = [],
  departments = [],
  loading = false,
  error = null,
  isAdmin = false,
  onEdit,
  onDelete,
  extraButton = null, // { label: string, onClick: function, icon: string }
  searchOptions = [
    { key: "lastName", label: "Nazwisko" },
    { key: "firstName", label: "Imię" },
    { key: "email", label: "Email" },
    { key: "department", label: "Dział" },
  ],
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("lastName");
  const [displayTitle, setDisplayTitle] = useState(title);
  const [sortedColumn, setSortedColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" lub "desc"
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [hover, setHover] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1380);
  const dropdownRef = useRef(null);

  // Rozpocznij edycję
  const handleEditClick = (item) => {
    setEditingId(item.id);
    setEditValues({
      firstName: item.firstName || "",
      lastName: item.lastName || "",
      internalPhoneNumber: item.internalPhoneNumber || "",
      phoneNumber: item.phoneNumber || "",
      mobilePhoneNumber: item.mobilePhoneNumber || "",
      email: item.email || "",
      department: item.department || "",
      departmentId:
        item.departmentId ||
        departments.find((d) => d.name === item.department)?.id ||
        "",
    });
  };

  const handleEditChange = (field, value) => {
    let processedValue = value;
    if (
      (field === "internalPhoneNumber" ||
        field === "phoneNumber" ||
        field === "mobilePhoneNumber") &&
      value === ""
    ) {
      processedValue = null;
    }
    setEditValues((prev) => ({ ...prev, [field]: processedValue }));
  };

  // Zatwierdź edycję
  const handleAgreeEdit = async (item) => {
    if (onEdit) {
      await onEdit(item, editValues);
      setEditingId(null);
      setEditValues({});
    }
  };

  // Anuluj edycję
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  // Usuwanie pracownika
  const handleDelete = (id, firstName, lastName, email) => {
    setSelectedEmployee({ id, firstName, lastName, email });
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (onDelete && selectedEmployee) {
      await onDelete(selectedEmployee);
      setShowConfirmation(false);
      setSelectedEmployee(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmation(false);
    setSelectedEmployee(null);
  };

  // Wyszukiwanie i sortowanie
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchByChange = (criterion) => {
    setSearchBy(criterion);
    setSearchTerm("");
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSort = (column) => {
    if (sortedColumn === column) {
      // Jeśli klikamy na tę samą kolumnę, zmieniamy kierunek sortowania
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Jeśli klikamy na nową kolumnę, ustawiamy ją jako posortowaną rosnąco
      setSortedColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortedData = () => {
    const data = [...contactData];
    if (sortedColumn) {
      return data.sort((a, b) => {
        const comparison = a[sortedColumn]?.localeCompare(
          b[sortedColumn],
          "pl",
          {
            sensitivity: "base",
          },
        );
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }
    return data;
  };

  const filteredContacts = getSortedData().filter((item) => {
    if (isSmallScreen) {
      // Na małych ekranach wyszukuj zarówno po imieniu, jak i po nazwisku
      const searchLower = searchTerm.toLowerCase();
      const firstName = item.firstName?.toLowerCase() || "";
      const lastName = item.lastName?.toLowerCase() || "";
      return firstName.includes(searchLower) || lastName.includes(searchLower);
    } else {
      // Na dużych ekranach wyszukuj według wybranego kryterium
      return item[searchBy]?.toLowerCase().includes(searchTerm.toLowerCase());
    }
  });

  // Zamknij dropdown po kliknięciu poza nim
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Wykryj małe ekrany
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1380);
    };

    handleResize(); // Sprawdź przy załadowaniu
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Zmień tytuł na skrócony na ekranach mniejszych niż 400px
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 400) {
        // Skróć tytuł: "Baza teleadresowa Niviss" -> "Baza Niviss"
        // lub "Baza teleadresowa Maritex" -> "Baza Maritex"
        const shortTitle = title.replace("Baza teleadresowa", "Baza");
        setDisplayTitle(shortTitle);
      } else {
        setDisplayTitle(title);
      }
    };

    handleResize(); // Sprawdź przy załadowaniu
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [title]);

  // Znajdź label dla aktualnego searchBy
  const getSearchByLabel = () => {
    const option = searchOptions.find((opt) => opt.key === searchBy);
    return option ? option.label.toLowerCase() : searchBy;
  };

  // Modal potwierdzenia usunięcia
  const renderDeleteModal = () => {
    if (!showConfirmation) return null;
    return createPortal(
      <div className="confirmation-overlay">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={handleCancelDelete}
          />
          <div className="areYouSure">
            <p>
              {t("contactDatabase.confirmDelete")} {selectedEmployee.firstName}{" "}
              {selectedEmployee.lastName}?
            </p>
          </div>
          <div className="confirmation-buttons">
            <button onClick={handleConfirmDelete}>{t("common.yes")}</button>
            <button onClick={handleCancelDelete}>{t("common.no")}</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  return (
    <div className="contact-container">
      <div className="header">
        <h1>{displayTitle}</h1>
        {isAdmin && createButtonLink && (
          <NavLink className="create-button" to={createButtonLink}>
            {createButtonLabel}
          </NavLink>
        )}
        <div className="header-right">
          {extraButton && (
            <div className="button-container">
              <button
                className="booking-button"
                onClick={extraButton.onClick}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
              >
                {extraButton.icon && (
                  <div
                    className="icon"
                    style={{
                      backgroundImage: `url(${
                        hover ? orangeSortIcon : extraButton.icon
                      })`,
                      transform: hover ? "scale(1.2)" : "rotate(270deg)",
                    }}
                  ></div>
                )}
                {extraButton.label}
              </button>
            </div>
          )}
          <div className="searching">
            <input
              type="text"
              placeholder={`Wyszukaj po ${getSearchByLabel()}...`}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <div
              className="search-icon"
              style={{ backgroundImage: `url(${searchIcon})` }}
            ></div>
            <div
              className={`dropdown ${isDropdownOpen ? "open" : ""}`}
              ref={dropdownRef}
            >
              <button className="search-toggle-btn" onClick={toggleDropdown}>
                {searchOptions.find((opt) => opt.key === searchBy)?.label ||
                  searchBy}
              </button>
              <div className="dropdown-content">
                {searchOptions.map((option) => (
                  <button
                    key={option.key}
                    className="dropdown-item"
                    onClick={() => handleSearchByChange(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="searching mobile-search">
          <input
            type="text"
            placeholder={`Wyszukaj po osobie...`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <div
            className="search-icon"
            style={{ backgroundImage: `url(${searchIcon})` }}
          ></div>
        </div>
      </div>

      {loading ? (
        <LoadingComponent />
      ) : error ? (
        <div className="error-message">Błąd: {error}</div>
      ) : filteredContacts.length > 0 ? (
        <table
          className="contact-table"
          style={{ fontSize: "1rem", minWidth: "1400px" }}
        >
          <thead>
            <tr>
              <th
                onClick={() => handleSort("lastName")}
                className={sortedColumn === "lastName" ? "sorted-column" : ""}
              >
                {t("contactDatabase.lastName")}
                <img
                  src={sortIcon}
                  alt="Sort Icon"
                  className={`sort-icon ${
                    sortedColumn === "lastName"
                      ? sortDirection === "asc"
                        ? "sort-asc"
                        : "sort-desc"
                      : ""
                  }`}
                />
              </th>
              <th
                onClick={() => handleSort("firstName")}
                className={sortedColumn === "firstName" ? "sorted-column" : ""}
              >
                {t("contactDatabase.firstName")}
                <img
                  src={sortIcon}
                  alt="Sort Icon"
                  className={`sort-icon ${
                    sortedColumn === "firstName"
                      ? sortDirection === "asc"
                        ? "sort-asc"
                        : "sort-desc"
                      : ""
                  }`}
                />
              </th>
              <th
                onClick={() => handleSort("internalPhoneNumber")}
                className={
                  sortedColumn === "internalPhoneNumber" ? "sorted-column" : ""
                }
              >
                {t("contactDatabase.internalPhone")}
                <img
                  src={sortIcon}
                  alt="Sort Icon"
                  className={`sort-icon ${
                    sortedColumn === "internalPhoneNumber"
                      ? sortDirection === "asc"
                        ? "sort-asc"
                        : "sort-desc"
                      : ""
                  }`}
                />
              </th>
              <th
                onClick={() => handleSort("phoneNumber")}
                className={
                  sortedColumn === "phoneNumber" ? "sorted-column" : ""
                }
              >
                {t("contactDatabase.phone")}
                <img
                  src={sortIcon}
                  alt="Sort Icon"
                  className={`sort-icon ${
                    sortedColumn === "phoneNumber"
                      ? sortDirection === "asc"
                        ? "sort-asc"
                        : "sort-desc"
                      : ""
                  }`}
                />
              </th>
              <th
                onClick={() => handleSort("mobilePhoneNumber")}
                className={
                  sortedColumn === "mobilePhoneNumber" ? "sorted-column" : ""
                }
              >
                {t("contactDatabase.mobilePhone")}
                <img
                  src={sortIcon}
                  alt="Sort Icon"
                  className={`sort-icon ${
                    sortedColumn === "mobilePhoneNumber"
                      ? sortDirection === "asc"
                        ? "sort-asc"
                        : "sort-desc"
                      : ""
                  }`}
                />
              </th>
              <th
                onClick={() => handleSort("email")}
                className={sortedColumn === "email" ? "sorted-column" : ""}
              >
                {t("contactDatabase.email")}
                <img
                  src={sortIcon}
                  alt="Sort Icon"
                  className={`sort-icon ${
                    sortedColumn === "email"
                      ? sortDirection === "asc"
                        ? "sort-asc"
                        : "sort-desc"
                      : ""
                  }`}
                />
              </th>
              <th
                onClick={() => handleSort("department")}
                className={sortedColumn === "department" ? "sorted-column" : ""}
              >
                {t("contactDatabase.department")}
                <img
                  src={sortIcon}
                  alt="Sort Icon"
                  className={`sort-icon ${
                    sortedColumn === "department"
                      ? sortDirection === "asc"
                        ? "sort-asc"
                        : "sort-desc"
                      : ""
                  }`}
                />
              </th>
              {isAdmin && <th>{t("contactDatabase.actions")}</th>}
            </tr>
          </thead>
          <tbody>
            {filteredContacts.map((item) => (
              <tr
                key={item.id}
                className={expandedRows.has(item.id) ? "expanded" : ""}
                onClick={
                  editingId === item.id
                    ? undefined
                    : () => toggleRowExpansion(item.id)
                }
              >
                {editingId === item.id ? (
                  <>
                    <td>
                      <input
                        type="text"
                        className="table-edit-input"
                        value={editValues.lastName}
                        onChange={(e) =>
                          handleEditChange("lastName", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="table-edit-input"
                        value={editValues.firstName}
                        onChange={(e) =>
                          handleEditChange("firstName", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="table-edit-input"
                        value={editValues.internalPhoneNumber}
                        onChange={(e) =>
                          handleEditChange(
                            "internalPhoneNumber",
                            e.target.value,
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="table-edit-input"
                        value={editValues.phoneNumber}
                        onChange={(e) =>
                          handleEditChange("phoneNumber", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="table-edit-input"
                        value={editValues.mobilePhoneNumber}
                        onChange={(e) =>
                          handleEditChange("mobilePhoneNumber", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="table-edit-input"
                        value={editValues.email}
                        onChange={(e) =>
                          handleEditChange("email", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <select
                        className="table-edit-input"
                        value={editValues.departmentId || ""}
                        onChange={(e) => {
                          const deptId = e.target.value;
                          const deptObj = departments.find(
                            (d) => d.id === deptId,
                          );
                          handleEditChange("departmentId", deptId);
                          handleEditChange("department", deptObj?.name || "");
                        }}
                      >
                        <option value="">Wybierz dział...</option>
                        {departments.map((dept, idx) => (
                          <option
                            key={dept?.id || `dept-${idx}`}
                            value={dept?.id || ""}
                          >
                            {dept?.name || "Nieznany dział"}
                          </option>
                        ))}
                      </select>
                    </td>
                    {isAdmin && (
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <img
                            className="delete-btn"
                            src={trashButtonIcon}
                            alt="Usuń"
                            style={{
                              cursor: "pointer",
                              width: "30px",
                              height: "30px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(
                                item.id,
                                item.firstName,
                                item.lastName,
                                item.email,
                              );
                            }}
                          />
                          <img
                            className="agree-btn"
                            src={agreeButtonIcon}
                            alt="Zatwierdź"
                            style={{
                              cursor: "pointer",
                              width: "30px",
                              height: "30px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAgreeEdit(item);
                            }}
                          />
                          <img
                            className="back-btn"
                            src={backButtonIcon}
                            alt="Anuluj"
                            style={{
                              cursor: "pointer",
                              width: "40px",
                              height: "40px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelEdit();
                            }}
                          />
                        </div>
                      </td>
                    )}
                  </>
                ) : (
                  <>
                    <td>
                      <img src={circleIcon} alt="" className="name-icon" />
                      {item.lastName}
                    </td>
                    <td>{item.firstName}</td>
                    <td>{item.internalPhoneNumber}</td>
                    <td>{item.phoneNumber}</td>
                    <td>{item.mobilePhoneNumber}</td>
                    <td>{item.email}</td>
                    <td>{item.department}</td>
                    {isAdmin && (
                      <td>
                        <img
                          className="delete-btn"
                          src={trashButtonIcon}
                          alt="Usuń"
                          style={{
                            cursor: "pointer",
                            width: "30px",
                            height: "30px",
                            marginRight: "10px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(
                              item.id,
                              item.firstName,
                              item.lastName,
                              item.email,
                            );
                          }}
                        />
                        <img
                          className="edit-btn"
                          src={editButtonIcon}
                          alt="Edytuj"
                          style={{
                            cursor: "pointer",
                            width: "36px",
                            height: "36px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(item);
                          }}
                        />
                      </td>
                    )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="no-absences">{t("contactDatabase.noEmployees")}</div>
      )}
      {renderDeleteModal()}
    </div>
  );
};

export default ContactTable;
